import { useEffect } from "react";
import { useRouteError, isRouteErrorResponse, Link } from "react-router-dom";
import { captureExceptionSafe } from "@/lib/sentry";

const RELOAD_KEY = "evohome:module-reload-at";
const RELOAD_WINDOW_MS = 60_000;

/** True when the browser could not fetch/evaluate a code chunk (deploy/server change, network drop). */
export function isModuleLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err ?? "");
  return /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Loading chunk .* failed|ChunkLoadError|Unable to preload CSS/i.test(msg);
}

/** Allows at most one automatic reload per minute, so a genuinely broken file never loops. */
export function claimOneReload(now = Date.now(), store: Storage = sessionStorage): boolean {
  try {
    const last = Number(store.getItem(RELOAD_KEY) || 0);
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    store.setItem(RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

export default function RouteErrorScreen() {
  const error = useRouteError();
  const moduleError = isModuleLoadError(error);

  useEffect(() => {
    captureExceptionSafe(error instanceof Error ? error : new Error(String(error)), {
      mechanism: moduleError ? "router.module-load" : "router.errorelement",
      url: window.location.pathname,
    });
  }, [error, moduleError]);

  const status = isRouteErrorResponse(error) ? `${error.status} ${error.statusText}` : null;
  const detail = error instanceof Error ? error.message : status;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-bold text-foreground">
          {moduleError ? "This page could not finish loading" : "Something went wrong"}
        </h1>
        <p className="text-muted-foreground">
          {moduleError
            ? "Part of the page could not be downloaded. This usually happens when the site has just been updated or the connection dropped. Reloading fetches the latest version."
            : "The page hit an unexpected error. It has been reported."}
        </p>
        {detail && (
          <p className="text-xs text-muted-foreground font-mono bg-muted p-3 rounded-lg text-left break-words">{detail}</p>
        )}
        <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={() => { claimOneReload(); window.location.reload(); }}
            className="min-h-11 px-6 rounded-lg bg-primary text-primary-foreground font-medium"
          >
            Reload page
          </button>
          <Link to="/" reloadDocument className="min-h-11 px-6 inline-flex items-center rounded-lg border border-border text-foreground font-medium">
            Go to homepage
          </Link>
        </div>
      </div>
    </div>
  );
}
